<template>
  <Teleport to="body">
    <transition name="insightdlg">
      <div
        v-if="dialog"
        class="insightdlg"
        role="dialog"
        aria-modal="true"
        @mousedown.self="$emit('close')"
      >
        <div class="insightdlg__panel">
          <header class="insightdlg__bar">
            <div class="insightdlg__bar-label">
              <span class="insightdlg__bar-mark" />
              <span>Slide</span>
            </div>
            <div class="insightdlg__bar-actions">
              <v-menu
                v-if="draft"
                :close-on-content-click="true"
                :z-index="3100"
                location="bottom end"
              >
                <template #activator="{ props: menuProps }">
                  <button
                    v-bind="menuProps"
                    type="button"
                    class="insightdlg__btn insightdlg__status"
                    :class="`insightdlg__status--${draft.status}`"
                    :disabled="readonly"
                    title="Status van dit inzicht"
                  >
                    <span class="insightdlg__status-eyebrow">Status</span>
                    <span class="insightdlg__status-square" />
                    <span class="insightdlg__status-value">{{
                      statusLabel
                    }}</span>
                    <Icon v-if="!readonly" name="chevron-down" :size="12" />
                  </button>
                </template>
                <v-list class="alice-menu-list">
                  <v-list-item
                    v-for="s in statuses"
                    :key="s.key"
                    :active="draft.status === s.key"
                    @click="setStatus(s.key)"
                  >
                    <template #prepend>
                      <span
                        class="insightdlg__status-square"
                        :class="`insightdlg__status-square--${s.key}`"
                      />
                    </template>
                    <v-list-item-title>{{ s.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <button
                type="button"
                class="insightdlg__btn insightdlg__btn--strong"
                :title="
                  copiedKey
                    ? 'Gekopieerd'
                    : 'Selecteer de slide en kopieer het inzicht'
                "
                @click="copyDraft"
              >
                {{ copiedKey ? "Gekopieerd" : "Kopieer" }}
              </button>
              <button
                v-if="!readonly"
                type="button"
                class="insightdlg__btn"
                @click="save"
              >
                Opslaan
              </button>
              <button
                type="button"
                class="insightdlg__close"
                title="Sluiten"
                @click="$emit('close')"
              >
                <Icon name="close" :size="16" />
              </button>
            </div>
          </header>

          <div
            v-if="draft"
            class="insightdlg__scroll"
            :class="{ 'insightdlg__scroll--dropping': dropping }"
            @paste="onPaste"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
          >
            <div ref="slide" class="insightdlg__slide">
              <div class="insightdlg__meta-row">
                <span class="insightdlg__crumb">{{ chapterCrumb }}</span>
              </div>

              <!-- inzicht (title) -->
              <div class="insightdlg__row insightdlg__row--title">
                <span class="insightdlg__row-label">inzicht</span>
                <div class="insightdlg__row-body">
                  <textarea
                    v-if="editing === 'title'"
                    ref="editor"
                    v-model="draft.title"
                    class="insightdlg__edit insightdlg__edit--title"
                    rows="1"
                    @input="autosize"
                    @blur="stopEditing"
                    @keydown.esc.prevent.stop="stopEditing"
                  />
                  <div
                    v-else
                    class="insightdlg__text insightdlg__text--title"
                    :class="{ 'insightdlg__text--placeholder': !draft.title }"
                    @click="startEditing('title')"
                  >
                    {{ draft.title || "Titel van het inzicht…" }}
                  </div>
                </div>
              </div>

              <!-- observaties (findings) -->
              <div
                v-for="(f, fi) in draft.findings"
                :key="'f' + fi"
                class="insightdlg__row insightdlg__row--finding"
              >
                <span class="insightdlg__row-label">observatie</span>
                <div class="insightdlg__row-body">
                  <textarea
                    v-if="editing === 'f' + fi"
                    ref="editor"
                    v-model="f.text"
                    class="insightdlg__edit insightdlg__edit--finding"
                    rows="1"
                    @input="autosize"
                    @blur="stopEditing"
                    @keydown.esc.prevent.stop="stopEditing"
                  />
                  <div
                    v-else
                    class="insightdlg__text insightdlg__text--finding"
                    :class="{ 'insightdlg__text--placeholder': !f.text }"
                    @click="startEditing('f' + fi)"
                  >
                    {{ f.text || "Observatie…" }}
                  </div>
                  <button
                    v-if="!readonly"
                    type="button"
                    class="insightdlg__row-remove"
                    title="Verwijder observatie"
                    @click="draft.findings.splice(fi, 1)"
                  >
                    ×
                  </button>
                </div>
              </div>
              <button
                v-if="!readonly"
                type="button"
                class="insightdlg__add"
                @click="addFinding"
              >
                + observatie
              </button>

              <!-- quotes -->
              <div
                v-for="(q, qi) in draft.quotes"
                :key="'q' + qi"
                class="insightdlg__row insightdlg__row--quote"
                :class="{ 'insightdlg__row--muted': !q.selected }"
              >
                <span class="insightdlg__row-label">quote</span>
                <div class="insightdlg__row-body">
                  <textarea
                    v-if="editing === 'q' + qi"
                    ref="editor"
                    v-model="q.text"
                    class="insightdlg__edit insightdlg__edit--quote"
                    rows="1"
                    @input="autosize"
                    @blur="stopEditing"
                    @keydown.esc.prevent.stop="stopEditing"
                  />
                  <div
                    v-else
                    class="insightdlg__text insightdlg__text--quote"
                    :class="{ 'insightdlg__text--placeholder': !q.text }"
                    @click="startEditing('q' + qi)"
                  >
                    {{ q.text || "Quote…" }}
                  </div>
                  <div class="insightdlg__quote-meta">
                    <input
                      v-if="editing === 'n' + qi"
                      ref="editor"
                      v-model="q.name"
                      class="insightdlg__edit insightdlg__edit--meta"
                      type="text"
                      placeholder="Respondent, kenmerk, tijdstip"
                      @blur="stopEditing"
                      @keydown.enter.prevent="stopEditing"
                      @keydown.esc.prevent.stop="stopEditing"
                    />
                    <span
                      v-else
                      class="insightdlg__meta-text"
                      :class="{ 'insightdlg__text--placeholder': !q.name }"
                      @click="startEditing('n' + qi)"
                      >{{ q.name || (readonly ? "" : "respondent…") }}</span
                    >
                    <button
                      v-if="!readonly"
                      type="button"
                      class="insightdlg__quote-toggle"
                      :title="
                        q.selected
                          ? 'Staat in het rapport; klik om te verbergen'
                          : 'Niet in het rapport; klik om te tonen'
                      "
                      @click="q.selected = !q.selected"
                    >
                      {{ q.selected ? "in rapport" : "niet in rapport" }}
                    </button>
                  </div>
                  <button
                    v-if="!readonly"
                    type="button"
                    class="insightdlg__row-remove"
                    title="Verwijder quote"
                    @click="draft.quotes.splice(qi, 1)"
                  >
                    ×
                  </button>
                </div>
              </div>
              <button
                v-if="!readonly"
                type="button"
                class="insightdlg__add"
                @click="addQuote"
              >
                + quote
              </button>

              <!-- afbeeldingen -->
              <div
                v-if="draft.images.length || uploads.length || !readonly"
                class="insightdlg__row insightdlg__row--images"
              >
                <span class="insightdlg__row-label">{{
                  draft.images.length === 1 ? "afbeelding" : "afbeeldingen"
                }}</span>
                <div class="insightdlg__row-body insightdlg__images">
                  <figure
                    v-for="(img, ii) in draft.images"
                    :key="img.path || img.url"
                    class="insightdlg__image"
                  >
                    <a
                      :href="img.url"
                      target="_blank"
                      rel="noopener"
                      :title="img.name || 'Open afbeelding'"
                    >
                      <img
                        :src="img.url"
                        :alt="img.name || ''"
                        loading="lazy"
                      />
                    </a>
                    <button
                      v-if="!readonly"
                      type="button"
                      class="insightdlg__image-remove"
                      title="Verwijder afbeelding"
                      @click="removeImage(ii)"
                    >
                      ×
                    </button>
                  </figure>
                  <div
                    v-for="u in uploads"
                    :key="u.id"
                    class="insightdlg__image insightdlg__image--uploading"
                    :title="u.name"
                  >
                    <img v-if="u.previewUrl" :src="u.previewUrl" alt="" />
                    <span
                      class="insightdlg__image-progress"
                      :style="{ width: Math.round(u.progress * 100) + '%' }"
                    />
                  </div>
                  <button
                    v-if="!readonly"
                    type="button"
                    class="insightdlg__image-add"
                    title="Afbeelding toevoegen — of plak / sleep er een op de slide"
                    @click="pickImages"
                  >
                    <Icon name="plus" :size="16" />
                    <span>afbeelding</span>
                  </button>
                  <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    @change="onFilesPicked"
                  />
                  <p v-if="imageError" class="insightdlg__image-error">
                    {{ imageError }}
                  </p>
                </div>
              </div>
            </div>

            <details
              v-if="projectUid && !readonly"
              class="insightdlg__refine"
              @toggle="refineOpen = $event.target.open"
            >
              <summary class="insightdlg__refine-head">
                <Icon name="star" :size="16" class="insightdlg__refine-icon" />
                <span class="insightdlg__refine-title">Refine with Alice</span>
                <span class="insightdlg__refine-hint">{{
                  refineOpen
                    ? "Voorstellen komen op de slide; Opslaan bewaart ze."
                    : "Laat Alice dit inzicht aanscherpen, onderbouwen met quotes, inkorten of vertalen."
                }}</span>
                <Icon
                  name="chevron-down"
                  :size="14"
                  class="insightdlg__refine-chev"
                />
              </summary>
              <InsightRefineChat
                :key="draft.uid || 'draft'"
                :insight="draft"
                :project-uid="projectUid"
                compact
                @apply="applyToDraft"
              />
            </details>

            <div v-if="!readonly" class="insightdlg__foot">
              <button
                type="button"
                class="insightdlg__delete"
                @click="$emit('delete')"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import Icon from "@/components/alice/Icon.vue";
import InsightRefineChat from "@/components/InsightRefineChat.vue";
import { copyFeedbackMixin } from "@/utils/clipboard";
import {
  insightToClipboardText,
  insightToClipboardHtml,
} from "@/utils/insightsMarkdown";
import {
  INSIGHT_STATUSES,
  INSIGHT_STATUS_LABELS,
  normalizeStatus,
  toImageRows,
} from "@/utils/insightsModel";
import {
  isImageFile,
  uploadInsightImage,
  deleteInsightImage,
  imagePathsRemoved,
} from "@/utils/insightImages";

// One insight as a report slide in a wide right-hand drawer: chapter crumb +
// status, the title as "inzicht", findings as "observatie" rows, quotes with
// their meta line.
// Text is edited in place (click a row -> textarea with the same type;
// blur commits to the draft). The draft is a deep copy; Opslaan emits it and
// the parent applies/persists it, Sluiten discards.
export default {
  name: "InsightDialog",
  components: { Icon, InsightRefineChat },
  mixins: [copyFeedbackMixin],
  props: {
    dialog: { type: Boolean, default: false },
    insight: { type: Object, default: null },
    // Needed for the refine chat (project research context); omit to hide it.
    projectUid: { type: String, default: "" },
    chapterTitle: { type: String, default: "" },
    chapterIndex: { type: Number, default: null },
  },
  emits: ["close", "delete", "save"],
  data() {
    return {
      draft: null,
      // Key of the row currently shown as an input: "title", "f<i>", "q<i>",
      // "n<i>" (quote meta). One at a time.
      editing: null,
      statuses: INSIGHT_STATUSES.map((key) => ({
        key,
        label: INSIGHT_STATUS_LABELS[key],
      })),
      // Images: in-flight uploads (shown as progress tiles), the object paths
      // uploaded while this slide was open (orphans if the user closes
      // without Opslaan), and the image rows the insight had when it opened
      // (removed ones are deleted from Storage on Opslaan).
      uploads: [],
      sessionUploads: [],
      originalImages: [],
      dropping: false,
      imageError: "",
      saved: false,
      refineOpen: false,
    };
  },
  computed: {
    readonly() {
      return this.$store.getters.isGuest;
    },
    statusLabel() {
      return INSIGHT_STATUS_LABELS[
        normalizeStatus(this.draft ? this.draft.status : "draft")
      ];
    },
    chapterCrumb() {
      const title = this.chapterTitle || "Zonder titel";
      if (typeof this.chapterIndex === "number" && this.chapterIndex >= 0) {
        return `Hoofdstuk ${String(this.chapterIndex + 1).padStart(
          2,
          "0"
        )} · ${title}`;
      }
      return title;
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        const copy = this.insight
          ? JSON.parse(JSON.stringify(this.insight))
          : null;
        if (copy) {
          copy.status = normalizeStatus(copy.status);
          if (!Array.isArray(copy.findings)) copy.findings = [];
          if (!Array.isArray(copy.quotes)) copy.quotes = [];
          copy.images = toImageRows(copy.images);
        }
        this.draft = copy;
        this.originalImages = copy ? copy.images.slice() : [];
        this.sessionUploads = [];
        this.uploads = [];
        this.imageError = "";
        this.saved = false;
        this.editing = null;
        this.refineOpen = false;
        document.addEventListener("keydown", this.onKeydown);
      } else {
        // Closed without Opslaan: the files uploaded meanwhile belong to
        // nothing — remove them again (best effort, in the background).
        if (!this.saved) {
          this.sessionUploads.forEach((path) => deleteInsightImage(path));
        }
        this.sessionUploads = [];
        this.uploads = [];
        this.draft = null;
        this.editing = null;
        document.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key !== "Escape") return;
      // Escape first leaves the row being edited, a second one closes.
      if (this.editing) {
        this.editing = null;
        return;
      }
      this.$emit("close");
    },
    editorEl() {
      const r = this.$refs.editor;
      return Array.isArray(r) ? r[0] : r || null;
    },
    startEditing(key) {
      if (this.readonly) return;
      this.editing = key;
      this.$nextTick(() => {
        const el = this.editorEl();
        if (!el) return;
        el.focus();
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch (err) {
          /* input types without selection support */
        }
        this.autosize({ target: el });
      });
    },
    stopEditing() {
      this.editing = null;
    },
    setStatus(key) {
      if (!this.draft || this.readonly) return;
      this.draft.status = key;
    },
    autosize(e) {
      const el = e && e.target;
      if (!el || el.tagName !== "TEXTAREA") return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    },
    addFinding() {
      this.draft.findings.push({ text: "" });
      this.startEditing(`f${this.draft.findings.length - 1}`);
    },
    addQuote() {
      this.draft.quotes.push({ text: "", selected: true });
      this.startEditing(`q${this.draft.quotes.length - 1}`);
    },
    // Select the slide's text (labels and controls are user-select: none) so
    // a plain Cmd+C copies exactly what is on the slide.
    selectAll() {
      this.editing = null;
      this.$nextTick(() => {
        const el = this.$refs.slide;
        if (!el || !window.getSelection) return;
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(el);
        sel.removeAllRanges();
        sel.addRange(range);
      });
    },
    save() {
      this.editing = null;
      if (!this.draft) return;
      // Images taken off the slide (existing ones, or uploaded and removed
      // again in this session) leave Storage now that the removal is final.
      const gone = imagePathsRemoved(
        [
          ...this.originalImages,
          ...this.sessionUploads.map((path) => ({ path })),
        ],
        this.draft.images
      );
      gone.forEach((path) => deleteInsightImage(path));
      this.saved = true;
      this.$emit("save", this.draft);
    },
    // ----- images -----
    pickImages() {
      const input = this.$refs.fileInput;
      if (input) {
        input.value = "";
        input.click();
      }
    },
    onFilesPicked(e) {
      const files = Array.from((e.target && e.target.files) || []);
      this.addFiles(files);
    },
    onPaste(e) {
      if (this.readonly || !e.clipboardData) return;
      const files = Array.from(e.clipboardData.files || []).filter(isImageFile);
      if (!files.length) return; // plain text pastes go to the row being edited
      e.preventDefault();
      this.addFiles(files);
    },
    onDragOver(e) {
      if (this.readonly) return;
      const types = Array.from((e.dataTransfer && e.dataTransfer.types) || []);
      if (!types.includes("Files")) return;
      e.dataTransfer.dropEffect = "copy";
      this.dropping = true;
    },
    onDragLeave(e) {
      // Only when leaving the scroll area itself, not a child.
      if (e.currentTarget && e.currentTarget.contains(e.relatedTarget)) return;
      this.dropping = false;
    },
    onDrop(e) {
      this.dropping = false;
      if (this.readonly || !e.dataTransfer) return;
      const files = Array.from(e.dataTransfer.files || []).filter(isImageFile);
      if (files.length) this.addFiles(files);
    },
    async addFiles(files) {
      if (this.readonly || !this.draft) return;
      const images = files.filter(isImageFile);
      if (files.length && !images.length) {
        this.imageError =
          "Alleen afbeeldingen (png, jpg, gif, webp) kunnen op een slide.";
        return;
      }
      this.imageError = "";
      await Promise.all(images.map((file) => this.uploadOne(file)));
    },
    async uploadOne(file) {
      const entry = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        progress: 0,
        previewUrl: URL.createObjectURL(file),
      };
      this.uploads.push(entry);
      try {
        const row = await uploadInsightImage({
          projectUid: this.projectUid,
          insightUid: this.draft.uid,
          file,
          onProgress: (p) => {
            entry.progress = p;
          },
        });
        // The slide may have been closed while uploading.
        if (!this.draft) {
          deleteInsightImage(row.path);
          return;
        }
        this.draft.images.push(row);
        this.sessionUploads.push(row.path);
      } catch (err) {
        console.error("Image upload failed:", err);
        this.imageError =
          (err && err.message) || "Uploaden van de afbeelding is mislukt.";
      } finally {
        URL.revokeObjectURL(entry.previewUrl);
        this.uploads = this.uploads.filter((u) => u.id !== entry.id);
      }
    },
    removeImage(index) {
      if (!this.draft) return;
      this.draft.images.splice(index, 1);
    },
    // One action: select the slide text (so the user sees what was taken)
    // and put it on the clipboard as text + HTML.
    copyDraft() {
      if (!this.draft) return;
      this.selectAll();
      this.copyWithFeedback("insight", {
        text: insightToClipboardText(this.draft),
        html: insightToClipboardHtml(this.draft),
      });
    },
    // The proposal lands on the draft only; Opslaan persists it via the
    // parent, exactly like a manual edit.
    applyToDraft(proposal) {
      if (!this.draft || !proposal) return;
      this.draft.title = proposal.title || "";
      if (proposal.topic) this.draft.topic = proposal.topic;
      this.draft.findings = (proposal.findings || []).map((f) => ({
        text: f.text,
      }));
      this.draft.quotes = (proposal.quotes || []).map((q) => {
        const row = { text: q.text, selected: !!q.selected };
        if (q.name) row.name = q.name;
        return row;
      });
      if (typeof proposal.strength === "number") {
        this.draft.strength = proposal.strength;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.insightdlg {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  background: rgba(20, 25, 25, 0.35);

  /* Right-hand drawer: wide, full height, so the slide has room to breathe. */
  &__panel {
    width: min(960px, 94vw);
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--vp-white);
    border-left: 1px solid var(--vp-black);
    border-radius: 0;
    box-shadow: -16px 0 40px rgba(0, 0, 0, 0.12);
  }

  /* ---- top bar ---- */
  &__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 56px;
    padding: 0 16px 0 20px;
    border-bottom: 1px solid var(--border-hairline);
    background: var(--vp-white);
    flex-shrink: 0;
  }

  &__bar-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--fg-3);
  }

  &__bar-mark {
    width: 4px;
    height: 16px;
    background: var(--vp-black);
  }

  &__bar-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__btn {
    height: 34px;
    padding: 0 14px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-2);
    transition: border-color var(--dur-fast) var(--ease-hover),
      color var(--dur-fast) var(--ease-hover);

    &:hover {
      border-color: var(--vp-black);
      color: var(--vp-black);
    }
  }

  &__btn--strong {
    border: 2px solid var(--vp-black);
    color: var(--vp-black);
  }

  &__close {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    color: var(--vp-black);

    &:hover {
      border-color: var(--vp-black);
    }
  }

  /* ---- slide ---- */
  &__scroll {
    overflow-y: auto;
    min-height: 0;
  }

  &__slide {
    padding: 40px 56px 28px 24px;
  }

  &__meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 22px 140px;
    user-select: none;
  }

  &__crumb {
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--fg-3);
  }

  /* Status dropdown in the bar: eyebrow + coloured square + value. */
  &__status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    margin-right: 8px;
    color: var(--vp-black);

    &:disabled {
      cursor: default;
    }
  }

  &__status-eyebrow {
    font-size: 10px;
    letter-spacing: 0.12em;
    color: var(--fg-3);
  }

  &__status-value {
    text-transform: none;
    letter-spacing: 0;
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 700;
  }

  &__status-square {
    display: inline-block;
    width: 9px;
    height: 9px;
    background: var(--vp-black);
    flex-shrink: 0;
  }
  &__status--review &__status-square,
  &__status-square--review {
    background: var(--vp-geel-100);
  }
  &__status--done &__status-square,
  &__status-square--done {
    background: var(--vp-groen-100);
  }
  &__status-square--draft {
    background: var(--vp-black);
  }

  &__row {
    display: grid;
    grid-template-columns: 120px 1fr;
    column-gap: 20px;
    align-items: start;
    margin-bottom: 20px;
  }

  &__row-label {
    padding-top: 6px;
    text-align: right;
    font-family: var(--font-ui);
    font-size: 11px;
    letter-spacing: 0.06em;
    color: var(--fg-3);
    user-select: none;
  }

  &__row-body {
    position: relative;
    min-width: 0;
    padding-left: 20px;
    border-left: 2px solid var(--border-hairline);
  }

  &__row--title &__row-body {
    border-left: 4px solid var(--vp-black);
    padding-left: 18px;
  }

  &__row--quote &__row-body {
    border-left-color: var(--vp-turquoise-100);
  }

  &__row--muted &__row-body {
    border-left-color: var(--vp-grijs-40);
  }
  &__row--muted &__text--quote {
    color: var(--fg-3);
  }

  &__text,
  &__edit {
    display: block;
    width: 100%;
    margin: 0;
    padding: 4px 6px 4px 0;
    font-family: var(--font-body);
    font-size: 18px;
    line-height: 1.5;
    color: var(--vp-black);
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__text {
    cursor: text;
    border-radius: 0;
    transition: background var(--dur-fast) var(--ease-hover);

    &:hover {
      background: var(--bg-muted);
    }
  }

  &__text--placeholder {
    color: var(--fg-3);
  }

  &__text--title,
  &__edit--title {
    font-family: var(--font-display);
    font-size: 34px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.15;
  }

  &__text--quote,
  &__edit--quote {
    font-family: var(--font-display);
    font-style: italic;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.3;
  }

  &__edit {
    border: none;
    outline: none;
    resize: none;
    overflow: hidden;
    background: var(--bg-muted);
    field-sizing: content;
  }

  &__quote-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 6px;
    min-height: 20px;
  }

  &__meta-text {
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--fg-3);
    cursor: text;
  }

  &__edit--meta {
    width: auto;
    min-width: 260px;
    padding: 2px 6px;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--fg-2);
  }

  &__quote-toggle {
    padding: 2px 8px;
    background: transparent;
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
    opacity: 0;
    transition: opacity var(--dur-fast) var(--ease-hover);
    user-select: none;
  }
  &__row:hover &__quote-toggle,
  &__quote-toggle:focus-visible {
    opacity: 1;
  }

  &__row-remove {
    position: absolute;
    top: 4px;
    right: -30px;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-display);
    font-size: 16px;
    line-height: 1;
    color: var(--fg-3);
    opacity: 0;
    transition: opacity var(--dur-fast) var(--ease-hover);
    user-select: none;
  }
  &__row:hover &__row-remove {
    opacity: 1;
  }
  &__row-remove:hover {
    color: var(--vp-rood-100);
    border-color: var(--vp-rood-100);
  }

  &__add {
    display: block;
    margin: -6px 0 22px 160px;
    padding: 4px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
    user-select: none;

    &:hover {
      color: var(--vp-black);
    }
  }

  /* ---- images ---- */
  &__scroll--dropping {
    outline: 2px dashed var(--vp-black);
    outline-offset: -8px;
  }

  &__row--images &__row-body {
    border-left-color: var(--border-hairline);
  }

  &__images {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 10px;
    padding-top: 4px;
    padding-bottom: 4px;
  }

  &__image {
    position: relative;
    margin: 0;
    width: 148px;
    height: 104px;
    background: var(--bg-muted);
    border: 1px solid var(--border-hairline);
    overflow: hidden;

    a {
      display: block;
      width: 100%;
      height: 100%;
    }
    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__image--uploading img {
    opacity: 0.45;
  }

  &__image-progress {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 3px;
    background: var(--vp-black);
    transition: width 0.15s ease;
  }

  &__image-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-display);
    font-size: 15px;
    line-height: 1;
    color: var(--fg-2);
    opacity: 0;
    transition: opacity var(--dur-fast) var(--ease-hover);
    user-select: none;
  }
  &__image:hover &__image-remove,
  &__image-remove:focus-visible {
    opacity: 1;
  }
  &__image-remove:hover {
    color: var(--vp-rood-100);
    border-color: var(--vp-rood-100);
  }

  &__image-add {
    width: 148px;
    height: 104px;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: transparent;
    border: 1px dashed var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
    user-select: none;

    &:hover {
      color: var(--vp-black);
      border-color: var(--vp-black);
    }
  }

  &__image-error {
    flex-basis: 100%;
    margin: 0;
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--vp-rood-100);
  }

  /* ---- below the slide ---- */
  /* A full-width bar, not a label: bordered, chevron, hover — so it reads
     as something you open. */
  &__refine {
    margin: 0 56px 20px 24px;
    border: 1px solid var(--border-hairline);
    background: var(--vp-white);
    transition: border-color var(--dur-fast) var(--ease-hover);

    &:hover,
    &[open] {
      border-color: var(--vp-black);
    }
  }

  &__refine-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    list-style: none;
    user-select: none;
    transition: background var(--dur-fast) var(--ease-hover);

    &::-webkit-details-marker {
      display: none;
    }

    &:hover {
      background: var(--bg-muted);
    }

    &:focus-visible {
      outline: 2px solid var(--border-focus);
      outline-offset: -2px;
    }
  }

  &__refine-icon {
    color: var(--vp-black);
  }

  &__refine-title {
    flex-shrink: 0;
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: 700;
    color: var(--vp-black);
  }

  &__refine-hint {
    flex: 1;
    min-width: 0;
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--fg-3);
  }

  &__refine-chev {
    color: var(--vp-black);
    transition: transform var(--dur-fast) var(--ease-out);
  }

  &__refine[open] &__refine-chev {
    transform: rotate(180deg);
  }

  &__foot {
    display: flex;
    justify-content: flex-start;
    padding: 0 24px 20px;
  }

  &__delete {
    padding: 6px 12px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-3);
    transition: background var(--dur-fast) var(--ease-hover),
      color var(--dur-fast) var(--ease-hover),
      border-color var(--dur-fast) var(--ease-hover);

    &:hover {
      background: var(--vp-rood-10);
      border-color: var(--vp-rood-100);
      color: var(--vp-rood-100);
    }
  }
}

/* Entrance: backdrop fades, drawer slides in from the right. Exit is faster. */
.insightdlg-enter-active {
  transition: opacity var(--dur-moderate) var(--ease-out);

  .insightdlg__panel {
    transition: transform var(--dur-moderate) var(--ease-out);
  }
}
.insightdlg-leave-active {
  transition: opacity var(--dur-exit) var(--ease-out);

  .insightdlg__panel {
    transition: transform var(--dur-exit) var(--ease-out);
  }
}
.insightdlg-enter-from,
.insightdlg-leave-to {
  opacity: 0;

  .insightdlg__panel {
    transform: translateX(32px);
  }
}
</style>
